const cardDefaults = new WeakMap();

function rememberCardDefaults(item) {
  if (cardDefaults.has(item)) return cardDefaults.get(item);

  const imageSlot = item.querySelector('[data-card-image-slot]');
  const title = item.querySelector('[data-card-title]')?.textContent?.trim() ?? '';
  const imageNodes = imageSlot ? Array.from(imageSlot.childNodes, (node) => node.cloneNode(true)) : [];
  const defaults = { title, imageNodes };
  cardDefaults.set(item, defaults);
  return defaults;
}

function resetCard(item) {
  const defaults = rememberCardDefaults(item);
  const title = item.querySelector('[data-card-title]');
  const overlayTitle = item.querySelector('[data-card-title-overlay]');
  const badge = item.querySelector('[data-card-badge]');
  const media = item.querySelector('[data-card-media]');
  const imageSlot = item.querySelector('[data-card-image-slot]');

  if (title) title.textContent = defaults.title;
  if (overlayTitle) overlayTitle.textContent = defaults.title;
  if (badge) {
    badge.textContent = '';
    badge.hidden = true;
  }
  if (media instanceof HTMLElement) media.style.setProperty('--luxury-mega-image-position', 'center');
  if (imageSlot) imageSlot.replaceChildren(...defaults.imageNodes.map((node) => node.cloneNode(true)));
}

function applyCardConfig(item, config) {
  const titleOverride = config.dataset.titleOverride?.trim() ?? '';
  const badgeValue = config.dataset.badge?.trim() ?? '';
  const position = config.dataset.imagePosition || 'center';
  const title = item.querySelector('[data-card-title]');
  const overlayTitle = item.querySelector('[data-card-title-overlay]');
  const badge = item.querySelector('[data-card-badge]');
  const media = item.querySelector('[data-card-media]');
  const imageSlot = item.querySelector('[data-card-image-slot]');
  const imageTemplate = config.querySelector('template[data-mega-card-image]');

  if (titleOverride) {
    if (title) title.textContent = titleOverride;
    if (overlayTitle) overlayTitle.textContent = titleOverride;
  }

  if (badge) {
    badge.textContent = badgeValue;
    badge.hidden = badgeValue === '';
  }

  if (media instanceof HTMLElement) media.style.setProperty('--luxury-mega-image-position', position);

  if (imageSlot && imageTemplate instanceof HTMLTemplateElement) {
    imageSlot.replaceChildren(imageTemplate.content.cloneNode(true));
  }
}

function configureCta(mega, config) {
  const layout = config?.dataset.layout || mega.dataset.layout || 'catalog';
  mega.dataset.layout = layout;

  const cardItems = Array.from(mega.querySelectorAll('[data-luxury-card-item]'));
  cardItems.forEach((item) => item.classList.remove('luxury-mega__item--cta-source'));

  const cta = mega.querySelector('[data-luxury-cta]');
  const ctaLink = mega.querySelector('[data-luxury-cta-link]');
  const ctaLabel = mega.querySelector('[data-luxury-cta-label]');

  if (!(cta instanceof HTMLElement) || !(ctaLink instanceof HTMLAnchorElement)) return;

  if (layout !== 'catalog_cta' || cardItems.length === 0) {
    cta.hidden = true;
    return;
  }

  const configuredKey = config?.dataset.ctaKey?.trim() ?? '';
  const source =
    cardItems.find((item) => configuredKey && item.dataset.cardKey === configuredKey) ?? cardItems[cardItems.length - 1];
  const sourceLink = source?.querySelector('a.luxury-mega-card');
  const sourceTitle = source?.querySelector('[data-card-title]')?.textContent?.trim() ?? '';

  if (!(source instanceof HTMLElement) || !(sourceLink instanceof HTMLAnchorElement)) {
    cta.hidden = true;
    return;
  }

  source.classList.add('luxury-mega__item--cta-source');
  ctaLink.href = sourceLink.href;
  if (ctaLabel) ctaLabel.textContent = sourceTitle;
  cta.hidden = false;
  cta.classList.toggle('luxury-mega__cta--button', config?.dataset.ctaStyle === 'button');
  cta.classList.toggle('luxury-mega__cta--link', config?.dataset.ctaStyle !== 'button');
}

function applyConfigToMega(mega, config) {
  const cards = Array.from(mega.querySelectorAll('[data-luxury-card-item]'));
  cards.forEach(resetCard);

  const columns = config.dataset.desktopColumns || 'auto';
  if (columns !== 'auto') mega.style.setProperty('--luxury-mega-columns', columns);

  const ratio = config.dataset.imageRatio?.trim();
  if (ratio) mega.style.setProperty('--luxury-mega-image-ratio', ratio);

  mega.dataset.textStyle = config.dataset.textStyle || 'below';

  const parentLink = mega.querySelector('[data-luxury-parent-link]');
  if (parentLink instanceof HTMLElement) {
    const shouldShow = config.dataset.showMobileParentLink !== 'false';
    parentLink.hidden = !shouldShow;
    const parentLabel = parentLink.querySelector('[data-luxury-parent-link-label]');
    const configuredLabel = config.dataset.mobileParentLinkLabel?.trim();
    if (parentLabel && configuredLabel) parentLabel.textContent = configuredLabel;
  }

  for (const cardConfig of config.querySelectorAll('[data-mega-card-config]')) {
    const key = cardConfig.dataset.navigationKey?.trim();
    if (!key) continue;
    const item = cards.find((candidate) => candidate.dataset.cardKey === key);
    if (item instanceof HTMLElement) applyCardConfig(item, cardConfig);
  }

  configureCta(mega, config);
}

class LuxuryMegaConfigs extends HTMLElement {
  #observer;

  connectedCallback() {
    this.apply();
    this.#observer = new MutationObserver(() => this.apply());
    this.#observer.observe(this, { childList: true, subtree: true, attributes: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  apply() {
    const header = this.closest('header-component');
    if (!header) return;

    for (const config of this.querySelectorAll('[data-mega-config]')) {
      if (config.dataset.enabled === 'false') continue;
      const triggerKey = config.dataset.triggerKey?.trim();
      if (!triggerKey) continue;

      for (const mega of header.querySelectorAll('[data-luxury-mega]')) {
        if (mega.dataset.triggerKey === triggerKey) applyConfigToMega(mega, config);
      }
    }
  }
}

if (!customElements.get('luxury-mega-configs')) {
  customElements.define('luxury-mega-configs', LuxuryMegaConfigs);
}
